import React, { useEffect, useState, useContext } from "react";
import { useMsal } from "@azure/msal-react";
import "./UserProfile.css";
import PropTypes from "prop-types";
import { Box, Typography, Divider, Grid } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PrimarySelect from "../Inputs/PrimarySelect/PrimarySelect";
import PrimaryButton from "../Buttons/PrimaryButton/PrimaryButton";
import SecurityCheck from "../SecurityCheck/SecurityCheck";
import AuditLog from "../AuditLog/AuditLog";
import { nullCheck } from "../../utils/dataHelpers";
import { handleAltEnter } from "../../utils/keyboardShortcutHelpers";
import { SecurityService } from "../../services/SecurityService";
import { AlertContext, UserRolesContext } from "../Dashboard/Dashboard";

export default function UserProfile({
  userId,
  handleUserUpdated
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [roleId, setRoleId] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState({});
  const [userContact, setUserContact] = useState({});
  const [isAdminUser, setIsAdminUser] = useState(false);
  const userRoles = useContext(UserRolesContext);

  const activeOptions = [
    {
      optionName: "Active",
      value: true
    },
    {
      optionName: "Not Active",
      value: false
    }
  ];

  const handleSave = () => {
    const updatedUser = { ...user };
    updatedUser.roleId = roleId;
    updatedUser.isActive = isActive;
    setIsLoading(true);

    SecurityService.UpdateUser(instance, updatedUser).then(res => {
      setIsLoading(false);
      setUser(res);
      handleUserUpdated(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  useEffect(() => {
    if (user.roleId && userRoles?.length) {
      setRoleId(user.roleId);
      setIsActive(user.isActive);
    }
  }, [user, userRoles]);

  useEffect(() => {
    if (userId) {
      setTimeout(() => {
        SecurityService.GetUser(instance, userId).then(res => {
          if (res.userIdMcld) {
            SecurityService.GetUserByIdMcLeod(instance, res.userIdMcld).then(resMc => {
              setUserContact(resMc);
            }).catch(error => {
              console.log(error);
            }).finally(() => {
              setUser(res);
            });
          }
        }).catch(error => {
          console.error(error);
          warning(error);
        });
      }, 1);
    }
  }, [userId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      handleAltEnter(e, isLoading, handleSave);
    };

    document.addEventListener("keydown", handleKeyDown);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLoading]);

  return (
    <>
      <Box sx={{ marginTop: "20px", paddingLeft: "20px" }}>
        <Box sx={{ display: "flex" }}>
          <AccountCircleIcon sx={{ fontSize: "120px", color: "black_solid.60" }} />
          <Box sx={{ display: "block" }}>
            <Typography variant="title" sx={{ color: "primary.60", margin: "5px 0 0 30px" }}>
              {user.userName}
            </Typography>
            <Box sx={{ margin: "10px 0 0 30px", display: "flex", alignItems: "center" }}>
              <SecurityCheck
                componentName="Admin"
                action="Update"
                callbackRequired={true}
                callback={setIsAdminUser}
              >
                <PrimarySelect
                  options={userRoles?.map((r) => (r))}
                  onChange={r => setRoleId(r)}
                  value={roleId}
                  width="160px"
                  variant="standard"
                  nameKey="roleName"
                  valueKey="roleId"
                  disabled={!user.isActive}
                />
                <span style={{ margin: "0 0 2px 20px" }}>
                  <PrimarySelect
                    options={activeOptions.map((r) => (r))}
                    onChange={a => setIsActive(a)}
                    value={isActive}
                    width="160px"
                    variant="standard"
                    nameKey="optionName"
                    valueKey="value"
                  />
                </span>
                <span style={{ marginLeft: "20px" }}>
                  <PrimaryButton
                    label="Save"
                    width="46px"
                    onClick={handleSave}
                    isLoading={isLoading}
                    disabled={isLoading}
                  />
                </span>
              </SecurityCheck>
              {!isAdminUser &&
                <>
                  <Typography variant="h3">
                    {nullCheck(user, "", "role", "roleName")}
                  </Typography>
                  <Typography variant="h3" sx={{ marginLeft: "30px" }}>
                    {isActive ? "Active" : "Not Active"}
                  </Typography>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{ backgroundColor: "black_solid.10" }}>
        <Grid container sx={{ padding: "20px 50px" }}>
          <Grid item xs={5} sx={{ padding: "0 10px" }}>
            <Typography
              variant="h3"
              color="success.60"
            >
              Contact
            </Typography>
            <Divider sx={{ height: "1px", margin: "10px 0" }} />
            <Box className="Profile-detail-container">
              <Typography
                variant="body1"
                color="black_solid.80"
              >
                Email:
              </Typography>
              <span className="Profile-detail-value">
                <Typography
                  variant="body1"
                  color="black_solid.80"
                >
                  {user.email == null ? "Not registered" : user.email}
                </Typography>
              </span>
            </Box>
            <Box className="Profile-detail-container">
              <Typography
                variant="body1"
                color="black_solid.80"
              >
                Phone Number:
              </Typography>
              <span className="Profile-detail-value">
                <Typography
                  variant="body1"
                  color="black_solid.80"
                  sx={{ fontWeight: 500 }}
                >
                  {userContact.phone == null ? "" : userContact.phone}
                </Typography>
              </span>
              <Typography
                variant="body1"
                color="black_solid.80"
              >
                <span style={{ paddingLeft: 10 }}> Ext:</span>
              </Typography>
              <span className="Profile-detail-value">
                <Typography
                  variant="body1"
                  color="black_solid.80"
                  sx={{ fontWeight: 500 }}
                >
                  {userContact.extension == null ? "" : userContact.extension}
                </Typography>
              </span>
            </Box>
            {/* <Box className="Profile-detail-container">
              <Typography
                variant="body1"
                color="black_solid.80"
              >
                Mobile:
              </Typography>
              <span className="Profile-detail-value">
                <Typography
                  variant="body1"
                  color="black_solid.80"
                  sx={{ fontWeight: 500 }}
                >
                  {userContact.cellPhone === null ? "" : userContact.cellPhone}
                </Typography>
              </span>
            </Box> */}
            {/*<Box className="Profile-detail-container">*/}
            {/*  <Typography*/}
            {/*    variant="body1"*/}
            {/*    color="black"*/}
            {/*  >*/}
            {/*    Birthday:*/}
            {/*  </Typography>*/}
            {/*  <span className="Profile-detail-value">*/}
            {/*    <Typography*/}
            {/*      variant="body1"*/}
            {/*      color="black"*/}
            {/*      sx={{ fontWeight: 500 }}*/}
            {/*    >*/}
            {/*      bday*/}
            {/*    </Typography>*/}
            {/*  </span>*/}
            {/*</Box>*/}
          </Grid>
          <Grid item xs={7} sx={{ padding: "0 10px" }}>
            {/*<Typography*/}
            {/*  variant="h3"*/}
            {/*  color="success.60"*/}
            {/*  sx={{ fontWeight: 600 }}*/}
            {/*>*/}
            {/*  Alerts*/}
            {/*</Typography>*/}
            {/*<Divider sx={{ height: "1px", margin: "10px 0" }} />*/}
          </Grid>
        </Grid>
      </Box>

      <AuditLog userId={userId} />
    </>
  );
}

UserProfile.propTypes = {
  userId: PropTypes.string,
  handleUserUpdated: PropTypes.func.isRequired
};